import React from 'react';

/**
 * Lightweight in-house icon set. Designed at 24x24 viewBox with stroke=1.6.
 * Keeps the bundle small (no MUI icons), consistent line weights, themable via currentColor.
 */
const base = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  width: '1em',
  height: '1em',
  'aria-hidden': true,
};

export const Icon = ({ name, size = 20, className = '', ...rest }) => {
  const path = paths[name];
  if (!path) return null;
  return (
    <svg {...base} width={size} height={size} className={className} {...rest}>
      {path}
    </svg>
  );
};

const paths = {
  arrowRight: (
    <>
      <path d="M5 12h14" />
      <path d="M13 5l7 7-7 7" />
    </>
  ),
  arrowUpRight: (
    <>
      <path d="M7 17 17 7" />
      <path d="M8 7h9v9" />
    </>
  ),
  check: <path d="M5 12.5l4 4 10-10" />,
  close: (
    <>
      <path d="M6 6l12 12" />
      <path d="M18 6 6 18" />
    </>
  ),
  menu: (
    <>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h10" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3a14 14 0 0 1 0 18" />
      <path d="M12 3a14 14 0 0 0 0 18" />
    </>
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </>
  ),
  moon: <path d="M21 12.8A8.5 8.5 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" />,
  sparkle: (
    <>
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4" />
      <path d="M5 5l2.5 2.5M16.5 16.5L19 19M19 5l-2.5 2.5M7.5 16.5 5 19" />
    </>
  ),
  shield: <path d="M12 3l8 3v6c0 5-3.4 8.4-8 9-4.6-.6-8-4-8-9V6l8-3z" />,
  cloud: <path d="M7 18a4 4 0 0 1-.5-7.97A6 6 0 0 1 18 11a3.5 3.5 0 0 1 0 7H7z" />,
  code: (
    <>
      <path d="M8 8 4 12l4 4" />
      <path d="M16 8l4 4-4 4" />
      <path d="M14 5l-4 14" />
    </>
  ),
  server: (
    <>
      <rect x="3" y="4" width="18" height="6" rx="2" />
      <rect x="3" y="14" width="18" height="6" rx="2" />
      <path d="M7 7h.01M7 17h.01" />
    </>
  ),
  cpu: (
    <>
      <rect x="6" y="6" width="12" height="12" rx="2" />
      <path d="M9 2v2M15 2v2M9 20v2M15 20v2M2 9h2M2 15h2M20 9h2M20 15h2" />
    </>
  ),
  monitor: (
    <>
      <rect x="3" y="4" width="18" height="12" rx="2" />
      <path d="M8 20h8M12 16v4" />
    </>
  ),
  zap: <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />,
  layers: (
    <>
      <path d="M12 2 2 8l10 6 10-6-10-6z" />
      <path d="M2 14l10 6 10-6" />
      <path d="M2 11l10 6 10-6" />
    </>
  ),
  chart: (
    <>
      <path d="M3 3v18h18" />
      <path d="M7 14l4-4 4 4 5-7" />
    </>
  ),
  compass: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m16 8-3 7-7 3 3-7 7-3z" />
    </>
  ),
  rocket: (
    <>
      <path d="M14 4c4 0 6 2 6 6-2 0-4 2-5 4l-4 4-4-4-2-2 4-4c2-1 4-3 5-4z" />
      <circle cx="14" cy="10" r="1.5" />
      <path d="m6 18-2 2 2 2 2-2" />
    </>
  ),
  users: (
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 19c1-3.5 3.7-5 6.5-5s5.5 1.5 6.5 5" />
      <circle cx="17" cy="8" r="2.5" />
      <path d="M21.5 18c-.5-2.5-2-3.7-4-4" />
    </>
  ),
  mail: (
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" />
    </>
  ),
  phone: <path d="M5 4h3l2 5-2.5 1.5a11 11 0 0 0 6 6L15 14l5 2v3a2 2 0 0 1-2 2c-9.4 0-17-7.6-17-17 0-1.1.9-2 2-2z" />,
  pin: (
    <>
      <path d="M12 22s7-7.5 7-13a7 7 0 1 0-14 0c0 5.5 7 13 7 13z" />
      <circle cx="12" cy="9" r="2.5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  plus: <path d="M12 5v14M5 12h14" />,
  minus: <path d="M5 12h14" />,
  star: <path d="M12 3l2.6 5.5 6.1.6-4.6 4.1 1.4 6-5.5-3.2L6.5 19l1.4-6L3.3 9.1l6.1-.6L12 3z" />,
  quote: (
    <>
      <path d="M6 17c0-4 2-6 5-6V7c-5 0-8 3-8 8v3h3v-1zM15 17c0-4 2-6 5-6V7c-5 0-8 3-8 8v3h3v-1z" />
    </>
  ),
  lock: (
    <>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </>
  ),
  command: (
    <>
      <path d="M9 5a2 2 0 1 1-2 2h10a2 2 0 1 1-2 2V7M15 17a2 2 0 1 0 2-2H7a2 2 0 1 0 2 2v-2" />
    </>
  ),
  chevronDown: <path d="m6 9 6 6 6-6" />,
  chevronUp: <path d="m6 15 6-6 6 6" />,
  database: (
    <>
      <ellipse cx="12" cy="5" rx="8" ry="3" />
      <path d="M4 5v6c0 1.7 3.6 3 8 3s8-1.3 8-3V5" />
      <path d="M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6" />
    </>
  ),
  globeNet: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 9h18M3 15h18M12 3c-3 3-3 15 0 18M12 3c3 3 3 15 0 18" />
    </>
  ),
  factory: (
    <>
      <path d="M3 21V11l5 3V11l5 3V8l8 13H3z" />
      <path d="M9 17h2M13 17h2M17 17h1" />
    </>
  ),
  heart: <path d="M12 21s-7-4.5-9.5-9C.7 8.7 3 5 6.5 5c2 0 3.5 1.5 5.5 3.5C13.5 6.5 15 5 17.5 5 21 5 23.3 8.7 21.5 12c-2.5 4.5-9.5 9-9.5 9z" />,
  truck: (
    <>
      <path d="M3 6h11v10H3z" />
      <path d="M14 9h4l3 3v4h-7z" />
      <circle cx="7" cy="18" r="2" />
      <circle cx="17" cy="18" r="2" />
    </>
  ),
  briefcase: (
    <>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </>
  ),
  store: (
    <>
      <path d="M3 9l1.5-5h15L21 9" />
      <path d="M3 9v11h18V9" />
      <path d="M9 9v3a3 3 0 1 1-6 0M9 9v3a3 3 0 0 0 6 0v-3M15 9v3a3 3 0 0 0 6 0v-3" />
    </>
  ),
  landmark: (
    <>
      <path d="M3 21h18M5 21V10M19 21V10M3 10l9-6 9 6M9 21v-7M15 21v-7" />
    </>
  ),
};

export default Icon;
